import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField, LoadingContent } from 'components'
import { getDeliveryTypes, getPackageTypes, getUSStateCodes } from 'controllers/fedEx'
import { getFedExShipment, saveFedExShipment } from 'controllers/michiganOutgoingTitles/index'
import { FedExDeliveryTypeDto, FedExPackageTypeDto, FedExShipmentDto, fedExShipmentInit, FedExUSStateCodeDto } from 'dtos/fedEx'
import React, { useEffect, useState } from 'react'
import { formatDateMoment, formatTimeMoment } from 'utils'
import FedExOutboundLabelDialog from './FedExOutboundLabelDialog'
import { enqueueSnackbar } from 'notistack'

interface OutgoingTitlesGroupFedexLabelCardProps {
  fedExShipment: FedExShipmentDto | undefined
  isLocked: boolean
  michiganOutgoingTitleGuid?: string
  setFedExShipment: React.Dispatch<React.SetStateAction<FedExShipmentDto | undefined>>
}

export default function OutgoingTitlesGroupFedexLabelCard({
  fedExShipment,
  isLocked,
  michiganOutgoingTitleGuid,
  setFedExShipment
}: OutgoingTitlesGroupFedexLabelCardProps) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [deliveryTypes, setDeliveryTypes] = useState<FedExDeliveryTypeDto[]>([])
  const [packageTypes, setPackageTypes] = useState<FedExPackageTypeDto[]>([])
  const [usStateCodes, setUSStateCodes] = useState<FedExUSStateCodeDto[]>([])

  const [isGettingDeliveryTypes, setIsGettingDeliveryTypes] = useState<boolean>(false)
  const [isGettingFedExShipment, setIsGettingFedExShipment] = useState<boolean>(false)
  const [isGettingPackageTypes, setIsGettingPackageTypes] = useState<boolean>(false)
  const [isGettingUSStateCodes, setIsGettingUSStateCodes] = useState<boolean>(false)
  const [isSavingFedExShipment, setIsSavingFedExShipment] = useState<boolean>(false)

  useEffect(() => {
    setIsGettingDeliveryTypes(true)
    getDeliveryTypes()
      .then(res => setDeliveryTypes(res.value))
      .finally(() => setIsGettingDeliveryTypes(false))

    setIsGettingPackageTypes(true)
    getPackageTypes()
      .then(res => setPackageTypes(res.value))
      .finally(() => setIsGettingPackageTypes(false))

    setIsGettingUSStateCodes(true)
    getUSStateCodes()
      .then(res => setUSStateCodes(res.value))
      .finally(() => setIsGettingUSStateCodes(false))
  }, [])

  useEffect(() => {
    if (michiganOutgoingTitleGuid && michiganOutgoingTitleGuid.trim().toUpperCase() !== 'NEW') {
      setIsGettingFedExShipment(true)
      getFedExShipment(michiganOutgoingTitleGuid)
        .then(res => setFedExShipment(res.value))
        .finally(() => setIsGettingFedExShipment(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [michiganOutgoingTitleGuid])

  return (
    <>
      <FedExOutboundLabelDialog
        deliveryTypes={deliveryTypes}
        initialValues={fedExShipment || fedExShipmentInit}
        isLoading={isSavingFedExShipment}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSavingFedExShipment(true)
          saveFedExShipment(michiganOutgoingTitleGuid!, values)
            .then(res => {
              setFedExShipment(res.value)
              setDialogOpen(false)
              enqueueSnackbar('FedEx Outbound Label Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setIsSavingFedExShipment(false)
            })
        }}
        open={dialogOpen}
        packageTypes={packageTypes}
        usStateCodes={usStateCodes}
      />

      <Grid item xs={12} mb={1}>
        <Card>
          <CardContent>
            <Grid container spacing={1} alignItems='flex-start'>
              <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm={true}>
                  <Typography fontWeight='bold' variant='h6'>
                    Outgoing Titles Group FedEx Label
                  </Typography>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button disabled={isLocked || !michiganOutgoingTitleGuid} endIcon={<Edit />} onClick={() => setDialogOpen(true)} variant='text'>
                    EDIT
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <LoadingContent isLoading={isGettingDeliveryTypes || isGettingFedExShipment || isGettingPackageTypes || isGettingUSStateCodes}>
                <Grid item xs={12}>
                  <LabelField
                    label='Tracking #'
                    value={fedExShipment?.trackingNumber}
                    onClick={() => {
                      if (fedExShipment?.trackingNumber) {
                        window.open(`https://fedex.com/wtrk/track/?trknbr=${fedExShipment.trackingNumber}`, '_blank')
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Package Type'
                    value={
                      fedExShipment?.packageType && packageTypes
                        ? packageTypes.filter(pt => pt.enumeration === fedExShipment.packageType)[0].packageType
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship Date' value={fedExShipment?.date ? formatDateMoment(fedExShipment.date) : undefined} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship Time' value={fedExShipment?.time ? formatTimeMoment(fedExShipment.time) : undefined} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Delivery Type'
                    value={
                      fedExShipment?.deliveryType && deliveryTypes
                        ? deliveryTypes.filter(dt => dt.enumeration === fedExShipment.deliveryType)[0].serviceType
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Weight' value={fedExShipment?.weightLbs ? `${fedExShipment.weightLbs} Lbs.` : undefined} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship To Name' value={fedExShipment?.recipientContactPersonName} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship To Phone #' value={fedExShipment?.recipientContactPhoneNumber} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship To Address' value={fedExShipment?.recipientAddressStreetLine1} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship To City' value={fedExShipment?.recipientAddressCity} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Ship To State'
                    value={
                      fedExShipment?.recipientAddressStateOrProvinceCode && usStateCodes
                        ? usStateCodes.filter(ussc => ussc.code === fedExShipment.recipientAddressStateOrProvinceCode)[0].state
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Ship To Postal Code' value={fedExShipment?.recipientAddressPostalCode} />
                </Grid>
              </LoadingContent>

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Edit Button:**

Clicking this button opens the FedEx Outbound Label Dialog.

**Tracking #:**

Reads from Michigan Outgoing Titles – Group Details.

This is the FedEx tracking number for the Outgoing Titles Group.

The tracking number is a clickable link that opens the FedEx website.

**Package Type:**

Displays the package type selected from the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship Date:**

Displays the Ship Date entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship Time:**

Displays the Ship Time Entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Delivery Type:**

Displays the selected Delivery Type entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Weight:**

Displays the weight entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship To Name:**

Displays the Ship To Name entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship To Address:**

Displays the Ship To Address entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship To City:**

Displays the Ship To City entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship To State:**

Displays the Ship To State entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.

**Ship To Postal Code:**

Displays the Ship To Postal Code entered in the FedEx Outbound Label Dialog.

If no value is entered, a “-” will be displayed.`}
                </GreenText>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </>
  )
}
